import { gql } from '@apollo/client';
import { Checkbox, FormControl, FormLabel, Text, useToast } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { fromError } from '~utils/errors';
import {
  ConfigDisplaySignalDetectionToggle_ConfigFragment,
  useConfigUpdateSignalDetectionMutation,
} from './__generated__/ConfigDisplaySignalDetection.graphql';

interface Props {
  config: ConfigDisplaySignalDetectionToggle_ConfigFragment;
  isDisabled?: boolean;
}

export function ConfigDisplaySignalDetectionToggle({ config, isDisabled = false }: Props) {
  const currentSignalDetection = useMemo(() => config?.signalDetection?.reported, [config]);
  // const analytics = useAnalyticsReporter();
  const { t } = useTranslation();
  const toast = useToast();
  const [updateSignalDetection, { loading }] = useConfigUpdateSignalDetectionMutation();
  const handleUpdateSignalDetection = useCallback(
    async (value: boolean) => {
      try {
        await updateSignalDetection({
          variables: {
            input: {
              configurationId: config.id,
              enable: value,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('signalDetectionErr'),
          description: fromError(err, 'UpdateSignalDetection'),
        });
      }
    },
    [config, toast, updateSignalDetection, t],
  );

  return (
    <FormControl>
      <FormLabel>
        {t('signalDetection')}
        <FormLabelPendingIndicator isPending={loading} />
      </FormLabel>

      <Text color="gray.500" mb="2">
        {t('signalDetectionText')}
      </Text>
      <Checkbox
        isChecked={currentSignalDetection}
        onChange={(e) => handleUpdateSignalDetection(e.target.checked)}
        isDisabled={isDisabled}
      >
        {t('enableSignalDetection')}
      </Checkbox>
    </FormControl>
  );
}

ConfigDisplaySignalDetectionToggle.graphql = {
  fragments: {
    ConfigDisplaySignalDetectionToggle_config: gql`
      fragment ConfigDisplaySignalDetectionToggle_config on ConfigurationModel {
        id
        signalDetection {
          reported
        }
        ...UseConfigRecommendedSettings_config
      }
    `,
    mutations: {
      ConfigUpdatePower: gql`
        mutation ConfigUpdateSignalDetection($input: ConfigurationUpdateSignalDetectionInput!) {
          configurationUpdateSignalDetection(input: $input) {
            id
            ...ConfigDisplaySignalDetectionToggle_config
            ...UseConfigRecommendedSettings_config
          }
        }
      `,
    },
  },
};
